import { KeyPress, KeypressStatistics, PlotSelection, DetectedWindow, HighlightZone, AnalysisResult } from '../types';

/**
 * Calculate streaming BPM from interval (ms).
 * In osu!, streaming BPM = 4 keypresses per beat (1/4 notes).
 * BPM = 60000 / (interval_ms * 4) = 15000 / interval_ms
 */
export function intervalToBpm(intervalMs: number): number {
    if (intervalMs <= 0) return 0;
    return 15000 / intervalMs;
}

/**
 * Calculate interval (ms) from BPM.
 */
export function bpmToInterval(bpm: number): number {
    if (bpm <= 0) return Infinity;
    return 15000 / bpm;
}

/**
 * Convert CPS (clicks per second) to streaming BPM.
 */
export function cpsToBpm(cps: number): number {
    return cps * 15;
}

/**
 * Calculate statistics for a set of keypresses.
 */
export function calculateStatistics(keypresses: KeyPress[]): KeypressStatistics {
    if (keypresses.length === 0) {
        return {
            totalInputs: 0,
            duration: 0,
            avgBpm: 0,
            avgHoldTime: 0,
            minHoldTime: 0,
            maxHoldTime: 0
        };
    }

    // Sort by start time
    const sorted = [...keypresses].sort((a, b) => a.startTime - b.startTime);

    // Calculate duration
    const duration = (sorted[sorted.length - 1].endTime - sorted[0].startTime) / 1000;

    // Calculate intervals between consecutive keypresses
    const intervals: number[] = [];
    for (let i = 1; i < sorted.length; i++) {
        const interval = sorted[i].startTime - sorted[i - 1].startTime;
        if (interval > 0) {
            intervals.push(interval);
        }
    }

    // Calculate average interval and BPM
    const avgInterval = intervals.length > 0
        ? intervals.reduce((sum, i) => sum + i, 0) / intervals.length
        : 0;
    const avgBpm = intervalToBpm(avgInterval);

    // Calculate hold time statistics
    const holdTimes = sorted.map(kp => kp.holdTime);
    const avgHoldTime = holdTimes.reduce((sum, h) => sum + h, 0) / holdTimes.length;
    const minHoldTime = Math.min(...holdTimes);
    const maxHoldTime = Math.max(...holdTimes);

    return {
        totalInputs: keypresses.length,
        duration,
        avgBpm,
        avgHoldTime,
        minHoldTime,
        maxHoldTime
    };
}

/**
 * Filter keypresses within a time selection.
 */
export function filterKeypressesBySelection(
    keypresses: KeyPress[],
    selection: PlotSelection
): KeyPress[] {
    const startMs = selection.startTime * 1000;
    const endMs = selection.endTime * 1000;

    return keypresses.filter(kp =>
        kp.startTime >= startMs && kp.startTime <= endMs
    );
}

/**
 * Sliding window rake detection algorithm.
 * Finds any window of at least durationThreshold seconds where
 * the average BPM exceeds bpmThreshold.
 */
export function detectRakeSlidingWindow(
    keypresses: KeyPress[],
    bpmThreshold: number,
    durationThreshold: number
): { detectedWindows: DetectedWindow[], highlightZones: HighlightZone[] } {
    if (keypresses.length < 2) {
        return { detectedWindows: [], highlightZones: [] };
    }

    // Sort by start time
    const sorted = [...keypresses].sort((a, b) => a.startTime - b.startTime);

    const detectedWindows: DetectedWindow[] = [];
    const durationThresholdMs = durationThreshold * 1000;

    // Sliding window approach: for each starting point, find windows that exceed threshold
    let i = 0;
    while (i < sorted.length - 1) {
        // Try to find a fast window starting at i
        const windowStartTime = sorted[i].startTime;

        // Find the end index where we have at least durationThreshold seconds
        let j = i + 1;
        while (j < sorted.length && (sorted[j].startTime - windowStartTime) < durationThresholdMs) {
            j++;
        }

        // If we reached the end without enough duration, break
        if (j >= sorted.length && (sorted[j - 1].endTime - windowStartTime) < durationThresholdMs) {
            i++;
            continue;
        }

        // Now check windows from i to j, j+1, j+2, etc. to find the best/longest rake window
        let bestWindow: DetectedWindow | null = null;
        let bestEndIndex = j;

        for (let endIdx = j; endIdx <= sorted.length; endIdx++) {
            const actualEnd = endIdx < sorted.length ? endIdx : sorted.length;
            const windowKeypresses = sorted.slice(i, actualEnd);

            if (windowKeypresses.length < 2) continue;

            const windowDuration = (sorted[actualEnd - 1].endTime - windowStartTime) / 1000;

            // Calculate average BPM for this window
            const intervals: number[] = [];
            for (let k = 1; k < windowKeypresses.length; k++) {
                const interval = windowKeypresses[k].startTime - windowKeypresses[k - 1].startTime;
                if (interval > 0) {
                    intervals.push(interval);
                }
            }

            if (intervals.length === 0) continue;

            const avgInterval = intervals.reduce((sum, int) => sum + int, 0) / intervals.length;
            const windowBpm = intervalToBpm(avgInterval);

            // Check if this window qualifies
            if (windowDuration >= durationThreshold && windowBpm >= bpmThreshold) {
                const windowHoldTimes = windowKeypresses.map(kp => kp.holdTime);
                const avgHoldTime = windowHoldTimes.reduce((sum, h) => sum + h, 0) / windowHoldTimes.length;

                // Keep extending to find the longest qualifying window
                bestWindow = {
                    start: windowStartTime / 1000,
                    end: sorted[actualEnd - 1].endTime / 1000,
                    bpm: windowBpm,
                    duration: windowDuration,
                    isDefiniteRake: true,
                    isSuspicious: true,
                    avgHoldTime,
                    inputCount: windowKeypresses.length
                };
                bestEndIndex = actualEnd;
            } else if (bestWindow && windowBpm < bpmThreshold * 0.9) {
                // BPM dropped significantly, stop extending
                break;
            }
        }

        if (bestWindow) {
            detectedWindows.push(bestWindow);
            // Skip to end of this window
            i = bestEndIndex;
        } else {
            i++;
        }
    }

    // Also check suspicious windows (80% of threshold)
    const suspiciousWindows = findSuspiciousWindows(sorted, bpmThreshold, durationThreshold, detectedWindows);

    // Generate highlight zones
    const allWindows = [...detectedWindows, ...suspiciousWindows];
    const highlightZones: HighlightZone[] = allWindows.map(window => ({
        start: window.start,
        end: window.end,
        color: window.isDefiniteRake ? 'red' : 'orange',
        label: window.isDefiniteRake
            ? `Rake: ${window.bpm.toFixed(0)} BPM for ${window.duration.toFixed(1)}s`
            : `Suspicious: ${window.bpm.toFixed(0)} BPM for ${window.duration.toFixed(1)}s`
    }));

    return { detectedWindows: allWindows, highlightZones };
}

/**
 * Find suspicious windows (80% of threshold) that aren't already detected.
 */
function findSuspiciousWindows(
    sorted: KeyPress[],
    bpmThreshold: number,
    durationThreshold: number,
    existingWindows: DetectedWindow[]
): DetectedWindow[] {
    const suspicious: DetectedWindow[] = [];
    const suspiciousThreshold = bpmThreshold * 0.8;
    const durationThresholdMs = durationThreshold * 1000;

    let i = 0;
    while (i < sorted.length - 1) {
        // Check if this index is already covered by an existing window
        const existingCoverage = existingWindows.find(w =>
            sorted[i].startTime / 1000 >= w.start && sorted[i].startTime / 1000 <= w.end
        );
        if (existingCoverage) {
            i++;
            continue;
        }

        const windowStartTime = sorted[i].startTime;

        // Find end index for duration threshold
        let j = i + 1;
        while (j < sorted.length && (sorted[j].startTime - windowStartTime) < durationThresholdMs) {
            j++;
        }

        if (j >= sorted.length && (sorted[sorted.length - 1].endTime - windowStartTime) < durationThresholdMs) {
            i++;
            continue;
        }

        // Check if this qualifies as suspicious
        const windowKeypresses = sorted.slice(i, Math.min(j + 1, sorted.length));
        if (windowKeypresses.length < 2) {
            i++;
            continue;
        }

        const intervals: number[] = [];
        for (let k = 1; k < windowKeypresses.length; k++) {
            const interval = windowKeypresses[k].startTime - windowKeypresses[k - 1].startTime;
            if (interval > 0) intervals.push(interval);
        }

        if (intervals.length === 0) {
            i++;
            continue;
        }

        const avgInterval = intervals.reduce((sum, int) => sum + int, 0) / intervals.length;
        const windowBpm = intervalToBpm(avgInterval);
        const windowDuration = (windowKeypresses[windowKeypresses.length - 1].endTime - windowStartTime) / 1000;

        if (windowBpm >= suspiciousThreshold && windowBpm < bpmThreshold && windowDuration >= durationThreshold) {
            const windowHoldTimes = windowKeypresses.map(kp => kp.holdTime);
            const avgHoldTime = windowHoldTimes.reduce((sum, h) => sum + h, 0) / windowHoldTimes.length;

            suspicious.push({
                start: windowStartTime / 1000,
                end: windowKeypresses[windowKeypresses.length - 1].endTime / 1000,
                bpm: windowBpm,
                duration: windowDuration,
                isDefiniteRake: false,
                isSuspicious: true,
                avgHoldTime,
                inputCount: windowKeypresses.length
            });
        }

        i++;
    }

    return suspicious;
}

/**
 * Calculate standard deviation
 */
function standardDeviation(values: number[]): number {
    if (values.length === 0) return 0;
    const avg = values.reduce((sum, v) => sum + v, 0) / values.length;
    const squareDiffs = values.map(v => Math.pow(v - avg, 2));
    return Math.sqrt(squareDiffs.reduce((sum, v) => sum + v, 0) / values.length);
}

/**
 * Burst pattern detection for slower rake tapping.
 *
 * Rake tapping creates a distinctive pattern:
 * - Bursts of very short hold times during lateral swipes (<20-25ms)
 * - Occasional longer hold times during direction changes (30ms+)
 * - High deviation in hold times within small windows
 */
export function detectRakeBurstPattern(
    keypresses: KeyPress[],
    shortHoldThreshold: number = 25,  // ms - very short hold times
    burstRatioThreshold: number = 0.7, // 70% of holds must be short
    deviationThreshold: number = 15,   // ms - high std dev indicates bursts
    minWindowDuration: number = 2.0    // seconds
): DetectedWindow[] {
    if (keypresses.length < 10) {
        return [];
    }

    const sorted = [...keypresses].sort((a, b) => a.startTime - b.startTime);
    const detectedWindows: DetectedWindow[] = [];
    const windowSizeMs = minWindowDuration * 1000;

    let i = 0;
    while (i < sorted.length) {
        const windowStart = sorted[i].startTime;
        const windowEnd = windowStart + windowSizeMs;

        // Collect keypresses in this window
        const windowKeypresses: KeyPress[] = [];
        let j = i;
        while (j < sorted.length && sorted[j].startTime <= windowEnd) {
            windowKeypresses.push(sorted[j]);
            j++;
        }

        if (windowKeypresses.length >= 10) {
            const holdTimes = windowKeypresses.map(kp => kp.holdTime);

            // Calculate metrics
            const shortHolds = holdTimes.filter(h => h < shortHoldThreshold).length;
            const shortRatio = shortHolds / holdTimes.length;
            const holdStd = standardDeviation(holdTimes);
            const avgHold = holdTimes.reduce((sum, h) => sum + h, 0) / holdTimes.length;

            // Calculate BPM
            const intervals: number[] = [];
            for (let k = 1; k < windowKeypresses.length; k++) {
                const interval = windowKeypresses[k].startTime - windowKeypresses[k - 1].startTime;
                if (interval > 0) intervals.push(interval);
            }

            let windowBpm = 0;
            if (intervals.length > 0) {
                const avgInterval = intervals.reduce((sum, int) => sum + int, 0) / intervals.length;
                windowBpm = intervalToBpm(avgInterval);
            }

            const actualDuration = (windowKeypresses[windowKeypresses.length - 1].endTime - windowKeypresses[0].startTime) / 1000;

            // Detect burst pattern:
            // 1. High ratio of short hold times (characteristic of rake)
            // 2. High deviation (bursts with occasional spikes)
            // 3. Average hold time is low
            const isBurstPattern = (
                shortRatio >= burstRatioThreshold &&
                holdStd >= deviationThreshold &&
                avgHold < shortHoldThreshold * 1.5
            );

            if (isBurstPattern) {
                detectedWindows.push({
                    start: windowKeypresses[0].startTime / 1000,
                    end: windowKeypresses[windowKeypresses.length - 1].endTime / 1000,
                    bpm: windowBpm,
                    duration: actualDuration,
                    isDefiniteRake: true,
                    isSuspicious: true,
                    avgHoldTime: avgHold,
                    inputCount: windowKeypresses.length
                });

                // Skip to end of detected window
                i = j;
                continue;
            }
        }

        i++;
    }

    // Merge overlapping windows
    return mergeOverlappingWindows(detectedWindows);
}

/**
 * Merge overlapping detection windows.
 */
function mergeOverlappingWindows(windows: DetectedWindow[]): DetectedWindow[] {
    if (windows.length <= 1) return windows;

    // Sort by start time
    const sorted = [...windows].sort((a, b) => a.start - b.start);
    const merged: DetectedWindow[] = [sorted[0]];

    for (const current of sorted.slice(1)) {
        const last = merged[merged.length - 1];

        // Check for overlap (with 0.5s tolerance)
        if (current.start <= last.end + 0.5) {
            // Merge: extend the last window
            merged[merged.length - 1] = {
                start: last.start,
                end: Math.max(last.end, current.end),
                bpm: Math.max(last.bpm, current.bpm),
                duration: Math.max(last.end, current.end) - last.start,
                isDefiniteRake: last.isDefiniteRake || current.isDefiniteRake,
                isSuspicious: true,
                avgHoldTime: (last.avgHoldTime + current.avgHoldTime) / 2,
                inputCount: last.inputCount + current.inputCount
            };
        } else {
            merged.push(current);
        }
    }

    return merged;
}

/**
 * Full client-side analysis using combined detection methods.
 * Uses both BPM-based sliding window and burst pattern detection.
 */
export function analyzeKeypressesLocally(
    keypresses: KeyPress[],
    bpmThreshold: number,
    durationThreshold: number
): AnalysisResult {
    const stats = calculateStatistics(keypresses);

    // Method 1: BPM-based sliding window detection
    const { detectedWindows: bpmWindows } = detectRakeSlidingWindow(
        keypresses,
        bpmThreshold,
        durationThreshold
    );

    // Method 2: Burst pattern detection (catches slower rake with short hold times)
    const burstWindows = detectRakeBurstPattern(
        keypresses,
        25,  // shortHoldThreshold - very short hold times
        0.7, // burstRatioThreshold - 70% of holds must be short
        15,  // deviationThreshold - high std dev indicates bursts
        Math.min(durationThreshold, 2.0)  // minWindowDuration
    );

    // Combine detected windows (avoid duplicates)
    let allWindows = [...bpmWindows];

    for (const burstWin of burstWindows) {
        const isDuplicate = allWindows.some(w =>
            Math.abs(w.start - burstWin.start) < 0.5 && Math.abs(w.end - burstWin.end) < 0.5
        );
        if (!isDuplicate) {
            allWindows.push(burstWin);
        }
    }

    // Merge overlapping windows
    if (allWindows.length > 1) {
        allWindows = mergeOverlappingWindows(allWindows);
    }

    // Generate highlight zones for all windows
    const allHighlightZones: HighlightZone[] = allWindows.map(window => ({
        start: window.start,
        end: window.end,
        color: window.isDefiniteRake ? 'red' : 'orange',
        label: window.isDefiniteRake
            ? `Rake: ${window.bpm.toFixed(0)} BPM, ${window.avgHoldTime.toFixed(0)}ms hold, ${window.duration.toFixed(1)}s`
            : `Suspicious: ${window.bpm.toFixed(0)} BPM, ${window.avgHoldTime.toFixed(0)}ms hold`
    }));

    const isRaking = allWindows.some(w => w.isDefiniteRake);

    return {
        isRaking,
        statistics: stats,
        detectedWindows: allWindows,
        highlightZones: allHighlightZones
    };
}

/**
 * Format BPM for display.
 */
export function formatBpm(bpm: number): string {
    if (bpm === 0) return '0';
    if (bpm > 9999) return '>9999';
    return bpm.toFixed(0);
}

/**
 * Format duration in seconds.
 */
export function formatDuration(seconds: number): string {
    if (seconds < 60) {
        return `${seconds.toFixed(2)}s`;
    }
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs.toFixed(1)}s`;
}

/**
 * Classify hold time for display.
 */
export function classifyHoldTime(holdTimeMs: number): 'fast' | 'normal' | 'slow' {
    if (holdTimeMs < 30) return 'fast';
    if (holdTimeMs < 80) return 'normal';
    return 'slow';
}